import { useMemo } from "react";

import { useTransactions } from "@/hooks/api/useTransactions";
import { useChainMap } from "@/hooks/data/useChainMap";
import { useProtocolMap } from "@/hooks/data/useProtocolMap";

interface Props {
  address?: string;
  protocolId?: string;
}

const STATUSES = {
  0: "processing",
  1: "confirmed",
  2: "reverted",
} as const;

export function useFormattedTransactions({ address, protocolId }: Props = {}) {
  const { transactions, isLoading: isTxLoading, isUpdating, setPage } = useTransactions({ address, protocolId }, { enabled: Boolean(protocolId) });
  const { chainMap, isLoading: isChainLoading } = useChainMap();
  const { protocolMap, isLoading: isProtocolLoading } = useProtocolMap();

  const isLoading = isTxLoading || isChainLoading || isProtocolLoading;

  const formattedTransactions = useMemo(() => {
    if (isLoading) return [];

    return transactions.map(tx => {
      const fromChain = chainMap[tx.from_chain];
      const toChain = chainMap[tx.to_chain];
      const protocol = protocolMap[tx.protocol];

      return {
        status: STATUSES[tx.status],
        type: tx.tx_type === 0 ? "deposit" : "withdrawal",
        hash: tx.transaction_hash,
        sender: tx.sender,
        targetVault: tx.vault,
        sourceChain: { name: fromChain.name, logo: fromChain.icon },
        targetChain: { name: toChain.name, logo: toChain.icon },
        protocol: { name: protocol?.name, logo: protocol?.graphics.icon ? protocol.graphics.icon : "/assets/images/placeholders/protocol.svg" },
        timestamp: tx.creation_timestamp,
        superformId: tx.superform_id,
      } as const;
    });
  }, [transactions, chainMap, protocolMap, isLoading]);

  return {
    isLoading,
    isUpdating,
    formattedTransactions,
    setPage,
  };
}
